import { RecurrenceDate } from 'app/core/store/order-detail/order-detail.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { Place } from '../../interfaces/place.interface';
import { Definition } from '../definitions/definition.interface';
import { CCREditMaterialLine } from '../edit-ccr-detail/ccr-detail.interface';

// INTERFACES *********

export interface DeliveryMethod {
  type: string;
  soldTo: Place;
  shippingCondition: Definition;
  to: Place;
  from: Place;
  incoterm: Definition;
  customerReferenceNumber: string;
  originalReferenceNumber?: string;
  senderReferenceNumber: string;
  transporter: string;
  licensePlate: string;
  remarks: string;
  remarksHandshaker: string;
  sealNumber?: string;
  clearing?: boolean;
  wholesaler?: Place;
}

export interface Quantity {
  numberExchangePallet?: number; // optional, otherwise incompatible with OrderDetailTO.lineItems interface
  numberOfPallets: number;
  packingQuantity: number;
}

export interface Material {
  internalId?: number;
  type: 'combination' | 'packing' | 'pallet';
  packingStatus: string;
  logisticsVarietyPacking: string;
  requestedBalance?: string;
  poNumberItemLevel: string;
  poItemNumber: string;
  contentId: string;
  lineReferenceId: string;
  isNew: boolean;
}

// TODO rename to PackingMaterial
export interface PackingMaterial extends Material {
  type: 'packing';
  difference?: number;
  packingId: string;
  packingQuantity: number; // disputeQuantity || definitiveQuantity || originalQuantity
  status?: string;// used for dispute management (flows)
  originalQuantity?: number; // quantity that registrar fills in
  definitiveQuantity?: number; // quantity that handshaker fills in
  disputeQuantity?: number; // quantity that dispute manager fills in (sum should be equal to sum of definitiveQuantity (RFC970)
  saldo?: number; // for dispute management: saldo for that packingId that needs to be distributed
  isEditable?: boolean; // for dispute management: if true, is editable
}

export type OrderMaterial = PackingMaterial | PalletMaterial | CombinationMaterial;

// TODO split up in PalletMaterial & CombinationMaterial
export interface PalletMaterial extends Material {
  type: 'pallet';
  palletId: string;
  numberOfPallets: number;
  difference?: Quantity;
  loaded?: Quantity;
  unloaded?: Quantity;
}

export interface CombinationMaterial extends Material {
  type: 'combination'
  packingId: string;
  packingQuantity: number;
  palletId: string;
  numberOfPallets: number;
  packingsPerPallet: number;
  difference?: Quantity;
  loaded?: Quantity;
  unloaded?: Quantity;
}

export function isPackingMaterial(material: Material): material is PackingMaterial {
  return material.type === 'packing';
}

export function isPalletMaterial(material: Material): material is PalletMaterial {
  return material.type === 'pallet';
}

export function isCombinationMaterial(material: Material): material is CombinationMaterial {
  return material.type === 'combination';
}

export const RECURRENCE_PATTERN_MONTHLY = 'monthly';
export const RECURRENCE_PATTERN_WEEKLY = 'weekly';
export const RECURRENCE_PATTERN_WHICH_DAY = 'whichDay';
export const RECURRENCE_PATTERN_NR_OF_DAY = 'nrOfDay';

export interface Planning {
  loadingDate: string | string[];
  unloadingDate: string | string[];
  requestedUnloadingDate: string | string[]; // was in flow-detail.reducer FLOWDETAIL_COPY_OPEN_FLOW_SUCCESS
  confirmedUnloadingDate: string | string[]; // was in flow-detail.reducer FLOWDETAIL_COPY_OPEN_FLOW_SUCCESS
  definitiveDate?: string;
}

export interface FlowPlanning extends Planning {
  flowDate?: string;
  transferDate?: string;
}
export interface OrderPlanning extends Planning {
  orderDate?: string;
  exchangePallets?: boolean;
  numberOfExchangePallets?: number;
  maxAmountOfPalletExchangeAvailable?: number;
  recurrence: {
    active: boolean;
    pattern: 'weekly' | 'nrOfDay' | 'whichDay';
    endDate: string;
    weekly: {
      monday?: boolean;
      tuesday?: boolean;
      wednesday?: boolean;
      thursday?: boolean;
      friday?: boolean;
      saturday?: boolean;
      sunday?: boolean;
      mondayRecurrence?: Definition;
      tuesdayRecurrence?: Definition;
      wednesdayRecurrence?: Definition;
      thursdayRecurrence?: Definition;
      fridayRecurrence?: Definition;
      saturdayRecurrence?: Definition;
      sundayRecurrence?: Definition;
    };
    monthly: {
      day: number;
      monthPeriodicy1: number;
      monthPeriodicy2: number;
      which: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH' | 'LAST';
      weekday: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
    };
  };
}

export interface RelocationPlanning extends Planning {
  orderDate?: string;
  quantity: number; // for relocations
  exchangePallets?: boolean;
  numberOfExchangePallets?: number;
  maxAmountOfPalletExchangeAvailable?: number;
}

interface EtmOrderNumber {
  etmOrderNumber: string;
}

export interface FlowDetail extends FlorderDetail<PackingMaterial, FlowPlanning> {
  sealNumber: string;
  handshaker?: boolean;
  registrar?: boolean;
  lateHandshake?: boolean;
  flowDateError?: string;
  flowDateErrorObject: { [key: string]: boolean };
  executeHandshake?: boolean;
}

export interface OrderDetail extends FlorderDetail<PalletMaterial | PackingMaterial | CombinationMaterial, OrderPlanning> {
  palletQuantity: number;
  createTemplate: boolean;
  templateId?: number;
  templateName: string;
  sealNumber: string;
  fullTruck: boolean;
  ccr?: {
    ccrNumber: string;
    sealNumber: string;
    open: boolean;
    accepted: boolean;
    declined: boolean;
    ccrLineItems: CCREditMaterialLine[];
  };
  documents?: OrderDocument[];
  singleRecurrence?: string;
  singleRecurrenceEtmOrderNumbers: EtmOrderNumber[];
}

export interface RelocationDetail extends FlorderDetail<PalletMaterial | PackingMaterial | CombinationMaterial, RelocationPlanning> {
  palletQuantity: number;
  createTemplate: boolean;
  templateId?: number;
  templateName: string;
  sealNumber: string;
  otherEtmOrderNumbers?: EtmOrderNumber[];
}

export interface FlorderDetail<MATERIAL extends Material, PLANNING extends Planning> {
  id?: any; // needed for templates
  customer: Customer;
  status: string;
  deliveryMethod: DeliveryMethod;
  materials: MATERIAL[];
  planning: PLANNING;
  etmOrderNumber: string;
  salesOrderNumber: string;
  deliveryNumber: string;
  shipmentNumbers: string[];
  registeredBy: string;
  globalType: string;
  updateTime: string;
  updateDate: string;
  updateBy: string;
  isLocked?: boolean;
}

export interface AvailablePickingDates {
  full: string[];
  nonFull: string[];
}

export type FlorderDetailState = 'create' | 'view' | 'create-confirm' | 'edit' | 'edit-confirm' | 'recurrence-dates';

export interface EditFlorderMode {
  type: 'flow' | 'order' | 'ccr';
  state: FlorderDetailState;
  editWindow: 'customer' | 'delivery' | 'detail' | 'planning';
  openingWindow?: 'customer' | 'delivery' | 'detail' | 'planning';
  createTemplate: boolean;
  editTemplate: boolean;
  material?: Material;
  loading: boolean;
  loadSuccess: boolean;
  saving: boolean;
  saveSuccess: boolean;
  saved: boolean;
  datesLoading: boolean;
}

export interface EditFlorderDetail {
  mode: EditFlorderMode;
  availablePickingDates: AvailablePickingDates;
}

export interface FlorderDetailTO {
  comments: string;
  confirmedLoadingDate: string;
  confirmedUnloadingDate: string;
  customerRefNumber: string;
  senderRefNumber: string;
  deliveryDateChanged: boolean;
  deliveryNumber: any;
  depotChanged: boolean;
  etmOrderNumber: string;
  exchangePallets: any;
  numberOfExchangePallets: number;
  from: {
    id: string;
    name: any;
    address?: any;
    closingTime?: any;
    openTime?: any;
    openingHours?: any;
  };
  globalType: string;
  invoiceNumber: any;
  isLocked: boolean;
  licensePlate: string;
  packingChanged: boolean;
  purchaseOrderType: any;
  quantityChanged: boolean;
  registeredBy: any;
  requestedLoadingDate: any;
  requestedUnloadingDate: string;
  salesOrderNumber: string;
  shipmentNumber: string[];
  soldTo: {
    id: string;
    name: any;
  };
  to: {
    id: string;
    name: any;
    address?: any;
    closingTime?: any;
    openTime?: any;
    openingHours?: any;
  };
  transport: string;
  transporter: any;
  updateBy: string;
  updateDate: string;
  updateTime: string;
}

export interface OrderDetailTO extends FlorderDetailTO {
  orderType: string;
  orderDate: string;
  orderStatus: any;
  incoterm?: string;
  definitiveDate: string;
  lineItems: {
    itemNumber: number;
    logisticsVarietyPacking: string;
    numberOfPallets: number;
    packingId: string;
    packingQuantity: number;
    packingStatus: string;
    packingsPerPallet: number;
    palletId: string;
    palletStatus: string;
  }[];
  ccr: {
    ccrNumber: string;
    sealNumber: string;
    approved: string;
    ccrLineItems: {
      itemNo: string;
      changeType: string;
      lineType: string;
      palletId: string;
      packingId: string;
      palletStatus: string;
      packingStatus: string;
      logisticsVarietyPacking: string;
      packingsPerPallet: string;
      noOfPallets: string;
      packingQuantity: string;
      noExchangePallet: string;
      loaded?: Quantity;
      unloaded?: Quantity;
    }[];
  };
  documents: OrderDocument[];
}

export interface FlowDetailTO extends FlorderDetailTO {
  flowType: string;
  flowDate: string;
  transferDate?: string;
  definitiveDate: string;
  clearing: string;
  handshakeDate: string;
  handshakeStatus: string;
  wholeSalerId: string;
  customerNumber?: string;
  originalRefNumber?: string;
  handshakeComments: string;
  executeHandshake?: boolean;
  flowLineItems: {
    itemNumber: number;
    logisticsVarietyPacking: string;
    packingId: string;
    packingStatus: string;
    contentId: string;
    lineReferenceId: string;
    originalQuantity: number;
    definitiveQuantity: number;
    differenceQuantity: number;
    handshakeStatus?: string;
  }[];
}

export interface CalculatedTruckLoad {
  orderLineInfo: {
    orderLines: {
      itemNumber: number;
      quantity: string;
    }[];
    quantity: string;
    maxAmountOfPalletExchangeAvailable: number;
    fullTruck: boolean;
    loadedEpp: number;
    maxEpp: number;
  };
  message: {
    description: string;
    type: string;
  };
}

export interface OrderDocument {
  documentId: string;
  extension: string;
  fileName: string;
  mimeType: string;
  type: string;
}

export interface HasRecurrenceDates {
  recurrenceDates?: RecurrenceDate[];
}
